import { ComponentProps } from "../registry";
import { useBound } from "../helpers";
import { BoundValue } from "../../types";

/**
 * Status → color semantics. When the surface does NOT declare a variant,
 * common status words map to the catalog's five variants:
 *   pending / waiting / in review …    → warning (amber)
 *   approved / completed / success …   → success (green)
 *   rejected / failed / cancelled …    → error   (red)
 *   available / new / in progress …    → info    (blue)
 *   anything else                      → neutral (gray)
 * An explicit `variant` prop always wins — apps keep full control.
 */
const STATUS_VARIANTS: Record<string, string> = {
  // amber — needs someone's attention
  pending: "warning", waiting: "warning", awaiting: "warning",
  in_review: "warning", "in review": "warning", review: "warning",
  on_hold: "warning", "on hold": "warning", paused: "warning",
  expiring: "warning", overdue: "warning",
  // green — good end states
  approved: "success", accepted: "success", active: "success",
  success: "success", succeeded: "success", completed: "success",
  complete: "success", done: "success", adopted: "success",
  paid: "success", delivered: "success", confirmed: "success",
  enabled: "success", online: "success", healthy: "success",
  verified: "success", granted: "success",
  // red — bad end states
  rejected: "error", denied: "error", declined: "error",
  failed: "error", failure: "error", error: "error",
  cancelled: "error", canceled: "error", blocked: "error",
  expired: "error", revoked: "error", offline: "error",
  // blue — informational / in motion
  available: "info", new: "info", open: "info", submitted: "info",
  in_progress: "info", "in progress": "info", running: "info",
  processing: "info", shipped: "info", scheduled: "info",
  draft: "info", sold: "info",
};

function inferVariant(text: string): string {
  const norm = text.trim().toLowerCase();
  return STATUS_VARIANTS[norm] ?? STATUS_VARIANTS[norm.replace(/[\s-]+/g, "_")] ?? "neutral";
}

export function StatusBadge({ fields, scope }: ComponentProps) {
  const text = useBound<string>(fields.text as BoundValue, scope) ?? "";
  const explicit = useBound<string>(fields.variant as BoundValue, scope);
  const variant = explicit ?? inferVariant(String(text));
  return <span className={`a2-badge variant-${variant}`}>{String(text)}</span>;
}
